import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
// Imported directly, not through the components/Inputs barrel: the barrel pulls
// in MetricPicker, which reaches all the way back round to ReportForm and adds
// an import cycle.
import Dropdown from 'components/Inputs/Dropdown';
import TextArea from 'components/Inputs/TextArea';
import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import ToastContext from 'contexts/ToastContext';

import { AiReadyContext } from './AiReadyProvider';
import { isDomainShipped } from './constants';
import saveReportAiConfig from './saveReportAiConfig';

// The domains + description editor, shared by the roster row and the "make this
// AI-ready" suggestion. One component because the two do the same thing to the
// same three fields; two would drift the moment either gained a rule.

const EditDiv = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  background-color: var(--color-bg-secondary);
`;

const AiReadyEditor = ({
  reportId,
  reportName,
  initialDomains,
  initialDescription,
  isCurrentlyAiReady,
  onSaved,
}: {
  reportId: string;
  reportName: string;
  initialDomains: string[];
  initialDescription: string;
  isCurrentlyAiReady: boolean;
  onSaved: () => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { showToast } = useContext(ToastContext);
  const { shippedDomains } = useContext(AiReadyContext);

  const [domains, setDomains] = useState<string[]>(initialDomains);
  const [description, setDescription] = useState<string>(initialDescription);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // A domain already on the report that the resolved version no longer ships
  // stays selectable, or saving an unrelated edit would quietly drop it.
  const options = [...new Set([...shippedDomains, ...initialDomains])].sort();

  const save = async (nextIsAiReady: boolean) => {
    setIsSaving(true);
    try {
      await saveReportAiConfig({
        accountId: selectedAccountId,
        reportId,
        userId: currentUserId,
        isAiReady: nextIsAiReady,
        domains,
        agentDescription: description,
      });
      showToast(
        nextIsAiReady
          ? `${reportName} saved`
          : `${reportName} is no longer AI-ready`,
      );
      onSaved();
    } catch {
      showToast(`Could not save ${reportName}`);
    }
    setIsSaving(false);
  };

  return (
    <EditDiv>
      <Typography.Body type="Label">Domains</Typography.Body>
      <Dropdown
        isMulti
        placeholder="Select domains..."
        options={options.map((domain) => ({
          key: domain,
          label: isDomainShipped({ domain, shippedDomains })
            ? domain
            : `${domain} (not in this version)`,
          value: domain,
          isSelected: domains.includes(domain),
          onSelected: () =>
            setDomains((current) =>
              current.includes(domain)
                ? current.filter((name) => name !== domain)
                : [...current, domain],
            ),
        }))}
      />
      <Typography.Body type="Label">Agent description</Typography.Body>
      <TextArea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe how the agent should use this report..."
      />
      {domains.length === 0 && (
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          With no domain, no domain skill routes to this report and the agent
          will never load it.
        </Typography.Body>
      )}
      <Row centerAlign spaceBetween style={{ marginTop: 8 }}>
        {isCurrentlyAiReady ? (
          <Button
            type="Danger Secondary"
            size="Small"
            label="Remove from the agent"
            isLoading={isSaving}
            testId={`ai-ready-remove-${reportId}`}
            onClick={() => save(false)}
          />
        ) : (
          <div />
        )}
        <Button
          type="Primary"
          size="Small"
          label={isCurrentlyAiReady ? 'Save' : 'Make AI-ready'}
          isLoading={isSaving}
          testId={`ai-ready-save-${reportId}`}
          onClick={() => save(true)}
        />
      </Row>
    </EditDiv>
  );
};

export default AiReadyEditor;
